package ai.jessie.web.documents

import ai.jessie.shared.documents.DocumentDetailResponse
import ai.jessie.shared.documents.DocumentHistoryEntryResponse
import ai.jessie.web.members.localTime
import ai.jessie.web.theme.LabelStyle
import ai.jessie.web.theme.MutedStyle
import ai.jessie.web.theme.RowStyle
import ai.jessie.web.theme.SmallStyle
import ai.jessie.web.theme.StackStyle
import ai.jessie.web.theme.SubListStyle
import androidx.compose.runtime.Composable
import com.varabyte.kobweb.silk.style.toAttrs
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Ol
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Ul

// B8: the source opens inline for PDF and images; every type can be downloaded.
@Composable
internal fun sourceLinks(organisationId: String, detail: DocumentDetailResponse) {
    Div(attrs = RowStyle.toAttrs()) {
        if (detail.mediaType in INLINE_TYPES)
            A(
                href = documentContentHref(organisationId, detail.documentId, inline = true),
                attrs = {
                    attr("target", "_blank")
                    attr("rel", "noopener noreferrer")
                },
            ) {
                Text("Quelle in neuem Tab öffnen")
            }
        A(href = documentContentHref(organisationId, detail.documentId, inline = false)) {
            Text("Quelle herunterladen")
        }
    }
}

// BR-134, BR-135, A8: the recorded payment and what a linked invoice means for it.
@Composable
internal fun paymentSummary(detail: DocumentDetailResponse) {
    val paidOn = detail.paidOn
    P(attrs = SmallStyle.toAttrs()) {
        Text(if (paidOn == null) "Keine Zahlung erfasst." else "Bezahlt am $paidOn.")
    }
    if (detail.linkedInvoice)
        P(attrs = listOf(SmallStyle, MutedStyle).toAttrs()) {
            Text(
                if (paidOn == null)
                    "Das Dokument ist mit einer Rechnung verknüpft. Eine Zahlung markiert beide mit " +
                        "demselben Datum als bezahlt; die Rechnung muss dafür versendet sein."
                else
                    "Die Zahlung ist mit der verknüpften Rechnung geteilt und kann nicht mehr " +
                        "geändert werden."
            )
        }
}

@Composable
internal fun retention(detail: DocumentDetailResponse) {
    val until = detail.retentionUntil ?: return
    P(attrs = listOf(SmallStyle, MutedStyle).toAttrs()) { Text("Aufbewahrungspflicht bis $until.") }
}

// Step 12: after a transition the document can be found in its new state's list.
@Composable
internal fun listLink(organisationId: String, detail: DocumentDetailResponse) {
    val filter = DocumentFilter.entries.firstOrNull { it.name == detail.status } ?: return
    P(attrs = SmallStyle.toAttrs()) {
        A(href = documentHref(organisationId, detail.documentId, filter)) {
            Text("In der Liste „${filter.label}“ anzeigen")
        }
    }
}

// Step 12 / B5: every recorded event, oldest first as the server ordered it.
@Composable
internal fun documentHistory(detail: DocumentDetailResponse, state: DocumentWorkflowUiState) {
    H3(attrs = LabelStyle.toAttrs()) { Text("Verlauf") }
    val history = state.history
    when {
        history == null ->
            P(attrs = listOf(SmallStyle, MutedStyle).toAttrs()) {
                Text("Der Verlauf ist gerade nicht verfügbar. Bitte lade das Dokument neu.")
            }
        history.isEmpty() ->
            P(attrs = listOf(SmallStyle, MutedStyle).toAttrs()) {
                Text("Noch keine Änderung erfasst.")
            }
        else ->
            Ol(attrs = listOf(SubListStyle, SmallStyle, StackStyle).toAttrs { id(HISTORY_ID) }) {
                history.forEach { Li { historyEntry(it, detail) } }
            }
    }
}

@Composable
private fun historyEntry(entry: DocumentHistoryEntryResponse, detail: DocumentDetailResponse) {
    Text(
        listOfNotNull(
                documentEventLabel(entry.eventType),
                entry.actorName ?: "Jessie",
                localTime(entry.occurredAt),
            )
            .joinToString(" · ")
    )
    if (entry.changes.isNotEmpty())
        Ul(attrs = SubListStyle.toAttrs()) {
            entry.changes.forEach { change ->
                Li {
                    Text(
                        "${documentFieldLabel(change.field)}: " +
                            "${documentChangeValue(change.field, change.previous, detail)} → " +
                            documentChangeValue(change.field, change.new, detail)
                    )
                }
            }
        }
}

private val INLINE_TYPES = setOf("application/pdf", "image/jpeg", "image/png")
private const val HISTORY_ID = "document-history"
